import React, { memo, useState, useMemo, useCallback, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  Tooltip,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import GroupIcon from "@mui/icons-material/Group";

function EmployeePickerDialog({ open, onClose, employeeOptions, selectedEmps, onConfirm }) {
  const [search, setSearch] = useState("");
  const [checked, setChecked] = useState(() =>
    new Set(selectedEmps.map((e) => String(e.key)))
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employeeOptions;
    return employeeOptions.filter(
      (e) =>
        String(e.key).toLowerCase().includes(q) ||
        String(e.label).toLowerCase().includes(q)
    );
  }, [search, employeeOptions]);

  const toggle = useCallback((key) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleConfirm = () => {
    const result = employeeOptions.filter((e) => checked.has(String(e.key)));
    onConfirm(result);
    onClose();
  };

  const handleClose = () => {
    setSearch("");
    setChecked(new Set(selectedEmps.map((e) => String(e.key))));
    onClose();
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((e) => checked.has(String(e.key)));

  const toggleAll = () => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((e) => next.delete(String(e.key)));
      } else {
        filtered.forEach((e) => next.add(String(e.key)));
      }
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1, fontWeight: 700, color: "#004d40" }}>
        Select Employees
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        <TextField
          autoFocus
          size="small"
          placeholder="Search by code or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mx: 2, mt: 1, mb: 0.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Divider sx={{ mt: 1 }} />

        {filtered.length > 0 && (
          <ListItemButton onClick={toggleAll} dense sx={{ px: 2 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Checkbox
                edge="start"
                checked={allFilteredSelected}
                indeterminate={
                  filtered.some((e) => checked.has(String(e.key))) && !allFilteredSelected
                }
                tabIndex={-1}
                disableRipple
                size="small"
              />
            </ListItemIcon>
            <ListItemText
              primary={allFilteredSelected ? "Deselect all" : "Select all"}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 600, color: "#004d40" }}
            />
          </ListItemButton>
        )}

        <Divider />

        <List dense disablePadding sx={{ overflowY: "auto", maxHeight: 380, flex: 1 }}>
          {filtered.map((emp) => {
            const isChecked = checked.has(String(emp.key));
            return (
              <ListItem key={emp.key} disablePadding>
                <ListItemButton onClick={() => toggle(String(emp.key))} dense>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={isChecked}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <span>
                        <span style={{ fontWeight: 700, color: "#00695c", marginRight: 6 }}>
                          {emp.key}
                        </span>
                        {emp.label}
                      </span>
                    }
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}

          {filtered.length === 0 && (
            <ListItem sx={{ justifyContent: "center", color: "text.secondary", fontSize: 13 }}>
              No employees found
            </ListItem>
          )}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={handleClose} size="small">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          size="small"
          sx={{ bgcolor: "#00695c", "&:hover": { bgcolor: "#004d40" } }}
        >
          Confirm ({checked.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const EmployeeMultiSelect = memo(function EmployeeMultiSelect({
  employeeOptions = [],
  selectedEmps = [],
  onChange,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
    
    <div
      onKeyDownCapture={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          e.target.blur(); // just close the dropdown, don't select anything
        }
      }}
    >
      <Autocomplete
        multiple
        size="small"
        options={employeeOptions}
        value={selectedEmps}
        onChange={(e, newVal) => onChange(newVal)}
        disableCloseOnSelect
        filterSelectedOptions
        getOptionLabel={(opt) => opt.label || ""}
        isOptionEqualToValue={(opt, val) => String(opt.key) === String(val.key)}
        sx={{ flex: 1, minWidth: 280 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Employees (multi-select)"
            placeholder="Search & select employees…"
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              width: "100%",
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  <InputAdornment position="end">
                    <Tooltip title="Browse all employees">
                      <IconButton
                        size="small"
                        onClick={() => setDialogOpen(true)}
                        sx={{ color: "#00695c" }}
                      >
                        <GroupIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                </>
              ),
            }}
          />
        )}
        renderTags={(value, getTagProps) => {
          const visible = value.slice(0, 1);
          const extra = value.length - 1;
          // for the name +1more to see employee's name 
          const hiddenNames = value.slice(1);
          return (
            <>
              {visible.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.key}
                  label={option.label}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: "rgba(0,121,107,.08)",
                    color: "#004d40",
                  }}
                />
              ))}
               {extra > 0 && (
                <Tooltip
                  title={
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                      {hiddenNames.map((o) => (
                        <span key={o.key}>{o.label}</span>
                      ))}
                    </Box>
                  }
                >
                  <Chip
                    label={`+${extra} more`}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: "rgba(0,121,107,.15)",
                      color: "#004d40",
                    }}
                  />
                </Tooltip>
              )}
            </>
          );
        }}
      />
      </div>

      <EmployeePickerDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        employeeOptions={employeeOptions}
        selectedEmps={selectedEmps}
        onConfirm={onChange}
      />
    </>
  );
});

export default EmployeeMultiSelect;
